/**
 * Engineering Reference Service
 *
 * Loads and manages engineering reference documentation content.
 * Documentation is converted from EnergyPlus LaTeX source using:
 *   python scripts/convert-energyplus-docs.py
 */

import type { EngineeringDocument } from '../types';

// Type for the index structure
interface EngineeringIndex {
  documents: Array<{
    id: string;
    title: string;
    category: string;
    description: string;
    relatedObjects: string[];
    file: string;
  }>;
  categories: Array<{
    id: string;
    name: string;
    description: string;
  }>;
}

// Use Vite's import.meta.glob to dynamically import all markdown files
const markdownModules = import.meta.glob('../data/engineering-reference/*.md', {
  query: '?raw',
  import: 'default',
  eager: true,
}) as Record<string, string>;

// Try to import index.json - this is optional
const indexModules = import.meta.glob('../data/engineering-reference/index.json', {
  eager: true,
}) as Record<string, { default: EngineeringIndex } | EngineeringIndex>;

// Get the engineering index if it exists
function getEngineeringIndex(): EngineeringIndex | null {
  const indexKey = '../data/engineering-reference/index.json';
  const indexModule = indexModules[indexKey];

  if (!indexModule) {
    return null;
  }

  // Handle both default export and direct export
  if ('default' in indexModule) {
    return indexModule.default;
  }
  return indexModule as EngineeringIndex;
}

/**
 * Get content for a file by its filename
 */
function getContentByFilename(filename: string): string {
  const key = `../data/engineering-reference/${filename}`;
  return markdownModules[key] || '';
}

/**
 * Get all engineering reference documents
 */
export function getEngineeringDocs(): EngineeringDocument[] {
  const index = getEngineeringIndex();
  if (!index) {
    return [];
  }

  return index.documents.map((doc) => ({
    id: doc.id,
    title: doc.title,
    category: doc.category,
    content: getContentByFilename(doc.file),
    relatedObjects: doc.relatedObjects,
  }));
}

/**
 * Get a specific engineering reference document by ID
 */
export function getEngineeringDoc(id: string): EngineeringDocument | null {
  const index = getEngineeringIndex();
  if (!index) {
    return null;
  }

  const docInfo = index.documents.find((d) => d.id === id);
  if (!docInfo) return null;

  return {
    id: docInfo.id,
    title: docInfo.title,
    category: docInfo.category,
    content: getContentByFilename(docInfo.file),
    relatedObjects: docInfo.relatedObjects,
  };
}

/**
 * Get engineering reference categories
 */
export function getEngineeringCategories(): Array<{
  id: string;
  name: string;
  description: string;
}> {
  const index = getEngineeringIndex();
  if (!index) {
    return [];
  }
  return index.categories;
}

/**
 * Get engineering docs by category
 */
export function getEngineeringDocsByCategory(category: string): EngineeringDocument[] {
  const index = getEngineeringIndex();
  if (!index) {
    return [];
  }

  return index.documents
    .filter((doc) => doc.category.toLowerCase() === category.toLowerCase())
    .map((doc) => ({
      id: doc.id,
      title: doc.title,
      category: doc.category,
      content: getContentByFilename(doc.file),
      relatedObjects: doc.relatedObjects,
    }));
}

/**
 * Find engineering docs related to an object type
 */
export function getEngineeringDocsForObject(objectType: string): EngineeringDocument[] {
  const index = getEngineeringIndex();
  if (!index) {
    return [];
  }

  const lowerObjectType = objectType.toLowerCase();
  return index.documents
    .filter((doc) =>
      doc.relatedObjects.some(
        (obj) => obj.toLowerCase() === lowerObjectType
      )
    )
    .map((doc) => ({
      id: doc.id,
      title: doc.title,
      category: doc.category,
      content: getContentByFilename(doc.file),
      relatedObjects: doc.relatedObjects,
    }));
}

/**
 * Check if engineering reference docs are available
 */
export function hasEngineeringDocs(): boolean {
  const index = getEngineeringIndex();
  return index !== null && index.documents.length > 0;
}
